import { getBaseRole } from '@/helpers/customRoles';
import Modal from '@common/Modal'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Button from '@UI/New/Button'
import ExpandableTextPanel from '@UI/New/ExpandableTextPanel'
import { usersAPI } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useAccountActions } from '@/hooks/useAccountActions'
import { actionUpdProfile, selectProfile } from '@store/profile.reducer'
import { closeModal } from '@store/modalsSlise'
import TYPES from '@modals/TYPES'
import sharedClasses from '../index.module.css'
import classes from './index.module.css'
import {
  type ChildDocumentsCompliance,
  type ComplianceDocumentState,
  type ComplianceRequiredDocument,
  type DocumentsCompliancePayload,
  getComplianceDocumentFileKey,
  getComplianceDeadlineSubtitle,
  getCompliancePopupDocuments,
  getComplianceDocumentStateView,
  getParentChildrenWithPopupCompliance,
  isRequiredDocumentUploaded,
  REQUIRED_DOCUMENTS_ACCESS_RESTORED_EVENT,
  REQUIRED_DOCUMENTS_COMPLIANCE_PROFILE_EXPAND,
} from '@/helpers/new/requiredDocumentsCompliance.utils'
import { AuthService, idUserAuthorizedOnTheAuthorizationPage } from '@/services/AuthService'
import headerClasses from '@common/Header/index.module.css'

type ComplianceDocument = ComplianceRequiredDocument

const NON_BLOCKING_REOPEN_INTERVAL_MS = 10 * 60 * 1000
const COMPLIANCE_MODAL_APPEARANCE = 'plain' as const
const DEADLINE_MODAL_TITLE = 'Требуется загрузить обязательные документы'
const BLOCKING_SUBTITLE_FALLBACK =
  'Загрузите необходимый перечень документов'

const isDocumentReadyForSubmit = (
  document: ComplianceDocument,
  selectedFiles: Record<string, File>,
  fileKey: string,
): boolean => {
  const state = (document?.state || 'not verified') as ComplianceDocumentState

  if (selectedFiles[fileKey]) {
    return true
  }

  if (state === 'rejected') {
    return false
  }

  return Boolean(document?.is_uploaded)
}

const areAllRequiredDocumentsUploaded = (
  documents: ComplianceDocument[],
): boolean =>
  documents.length > 0 && documents.every(isRequiredDocumentUploaded)

const getDocumentSupportComment = (document: ComplianceDocument): string => {
  const raw = document?.support_comment
  return typeof raw === 'string' ? raw.trim() : ''
}

const getDocumentStateClassByTone = (tone: string): string => {
  switch (tone) {
    case 'selected':
      return classes.stateSelected
    case 'approved':
      return classes.stateApproved
    case 'rejected':
      return classes.stateRejected
    case 'pending':
      return classes.statePending
    default:
      return classes.stateNotUploaded
  }
}

const getDocumentRowClassByTone = (tone: string): string => {
  switch (tone) {
    case 'selected':
      return classes.documentRowSelected
    case 'approved':
      return classes.documentRowApproved
    case 'rejected':
      return classes.documentRowRejected
    case 'pending':
      return classes.documentRowPending
    default:
      return classes.documentRowNotUploaded
  }
}

const RequiredDocumentsComplianceModal = () => {
  const dispatch = useDispatch()
  const addAlert = useAddAlert()
  const { loadingAccounts, disconnectAccount, onLogin } = useAccountActions()
  const location = useLocation()
  const profile = useSelector(selectProfile) as any
  const role = profile?.role
  const profileId = profile?.id
  const canUseCompliance = getBaseRole(role) === 'children' || getBaseRole(role) === 'parent'
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const [selectedFiles, setSelectedFiles] = useState<Record<string, File>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isComplianceReady, setIsComplianceReady] = useState(false)
  const [dismissedUntilTs, setDismissedUntilTs] = useState(0)
  const prevShouldAutoCloseRef = useRef(false)
  const prevShowPopupRef = useRef<boolean | null>(null)
  const isRestoringAccessRef = useRef(false)
  const isManuallyOpened = useSelector((state: any) =>
    Boolean(state?.modals?.types?.includes(TYPES.REQUIRED_DOCUMENTS_COMPLIANCE)),
  )

  const compliance = profile?.documents_compliance as
    | DocumentsCompliancePayload
    | undefined
  const childrenWithPopup = useMemo(
    () =>
      getBaseRole(role) === 'parent' ? getParentChildrenWithPopupCompliance(compliance) : [],
    [compliance, role],
  )
  const isParentSequentialFlow =
    getBaseRole(role) === 'parent' && childrenWithPopup.length > 0
  const activeChildCompliance = isParentSequentialFlow
    ? childrenWithPopup[0]
    : null
  const activeChildId = activeChildCompliance
    ? Number((activeChildCompliance as ChildDocumentsCompliance).child_id)
    : null
  const activeCompliance = isParentSequentialFlow
    ? activeChildCompliance
    : compliance ?? null
  const documents = useMemo(
    () => getCompliancePopupDocuments(activeCompliance),
    [activeCompliance],
  )
  const canClosePopup = Boolean(activeCompliance?.can_close_popup)
  const isDeadlineMode = canClosePopup
  const shouldShowPopup = isParentSequentialFlow
    ? childrenWithPopup.length > 0
    : Boolean(compliance?.show_popup)
  const showPopupRef = useRef(Boolean(shouldShowPopup))

  useEffect(() => {
    showPopupRef.current = Boolean(shouldShowPopup)
  }, [shouldShowPopup])

  useEffect(() => {
    setSelectedFiles({})
  }, [activeChildId])

  const restorePlatformAccess = useCallback(async () => {
    if (!profileId || isRestoringAccessRef.current) return

    isRestoringAccessRef.current = true

    try {
      try {
        await AuthService.refreshToken()
      } catch {
        // Продолжаем даже если refresh не удался.
      }

      const response = await usersAPI.getUser({
        id: profileId,
        expand: [...REQUIRED_DOCUMENTS_COMPLIANCE_PROFILE_EXPAND],
      })

      if (response?.data) {
        dispatch(actionUpdProfile(response.data))
      }

      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new Event(REQUIRED_DOCUMENTS_ACCESS_RESTORED_EVENT),
        )
        window.dispatchEvent(new Event('requiredDocumentsComplianceUpdated'))
      }
    } catch {
      if (typeof window !== 'undefined') {
        window.location.reload()
      }
    } finally {
      isRestoringAccessRef.current = false
    }
  }, [dispatch, profileId])

  const refreshCompliance = useCallback(async () => {
    if (!profileId || !canUseCompliance) return undefined

    const wasShowingPopup = Boolean(showPopupRef.current)

    try {
      const response = await usersAPI.getUser({
        id: profileId,
        expand: ['documents_compliance'],
      })

      // Всегда берём свежий ответ: если бэк не прислал compliance — сбрасываем,
      // иначе в Redux/localStorage остаётся старый show_popup.
      const nextCompliance = response?.data?.documents_compliance ?? null
      const nextChildrenWithPopup =
        getBaseRole(role) === 'parent'
          ? getParentChildrenWithPopupCompliance(nextCompliance)
          : []
      const nextShouldShowPopup =
        getBaseRole(role) === 'parent' && nextChildrenWithPopup.length > 0
          ? true
          : Boolean(nextCompliance?.show_popup)

      dispatch(
        actionUpdProfile({
          documents_compliance: nextCompliance,
        }),
      )

      const isAccessRestored = wasShowingPopup && !nextShouldShowPopup

      if (isAccessRestored) {
        prevShowPopupRef.current = false
        await restorePlatformAccess()
      } else {
        prevShowPopupRef.current = nextShouldShowPopup
      }

      setIsComplianceReady(true)
      return nextCompliance
    } catch {
      // Без успешного ответа попап по старым данным не открываем.
      return undefined
    }
  }, [canUseCompliance, dispatch, profileId, restorePlatformAccess, role])

  useEffect(() => {
    if (!canUseCompliance || !profileId) return
    refreshCompliance()
  }, [canUseCompliance, profileId, refreshCompliance])

  useEffect(() => {
    if (!canUseCompliance || !profileId || !isDeadlineMode) return
    refreshCompliance()
  }, [canUseCompliance, isDeadlineMode, location.pathname, profileId, refreshCompliance])

  useEffect(() => {
    if (isDeadlineMode && location.pathname === '/') {
      setDismissedUntilTs(0)
    }
  }, [isDeadlineMode, location.pathname])

  const allDocumentsUploaded = useMemo(() => {
    if (isDeadlineMode && isParentSequentialFlow) {
      return childrenWithPopup.every((child) =>
        areAllRequiredDocumentsUploaded(getCompliancePopupDocuments(child)),
      )
    }

    return areAllRequiredDocumentsUploaded(documents)
  }, [childrenWithPopup, documents, isDeadlineMode, isParentSequentialFlow])
  const shouldAutoCloseInDeadlineMode = isDeadlineMode && allDocumentsUploaded
  const canReopenByTime = Date.now() >= dismissedUntilTs
  const shouldShowAutomatically =
    shouldShowPopup &&
    documents.length > 0 &&
    !shouldAutoCloseInDeadlineMode &&
    (!canClosePopup || canReopenByTime)
  const shouldShowModal =
    documents.length > 0 &&
    !shouldAutoCloseInDeadlineMode &&
    ((isComplianceReady && shouldShowAutomatically) || isManuallyOpened)

  useEffect(() => {
    setIsComplianceReady(false)
  }, [profileId])

  const canSubmit = useMemo(
    () =>
      documents.length > 0 &&
      documents.every((document) => {
        const fileKey = getComplianceDocumentFileKey(document, activeChildId)
        return isDocumentReadyForSubmit(document, selectedFiles, fileKey)
      }),
    [activeChildId, documents, selectedFiles],
  )

  useEffect(() => {
    if (shouldShowPopup) {
      setDismissedUntilTs(0)
      return
    }

    setSelectedFiles({})
  }, [shouldShowPopup])

  useEffect(() => {
    setSelectedFiles((prev) => {
      const documentKeys = new Set(
        documents.map((item) =>
          getComplianceDocumentFileKey(item, activeChildId),
        ),
      )
      const nextEntries = Object.entries(prev).filter(([key]) =>
        documentKeys.has(key),
      )

      if (nextEntries.length === Object.keys(prev).length) {
        return prev
      }

      return Object.fromEntries(nextEntries)
    })
  }, [activeChildId, documents])

  useEffect(() => {
    if (!isComplianceReady) return

    const shouldAutoCloseNow = shouldAutoCloseInDeadlineMode
    const justBecameAutoClose =
      shouldAutoCloseNow && !prevShouldAutoCloseRef.current

    prevShouldAutoCloseRef.current = shouldAutoCloseNow

    if (!justBecameAutoClose) return

    if (isManuallyOpened) {
      dispatch(closeModal({ type: TYPES.REQUIRED_DOCUMENTS_COMPLIANCE }))
    }

    setSelectedFiles({})
    setDismissedUntilTs(Date.now() + NON_BLOCKING_REOPEN_INTERVAL_MS)
  }, [
    dispatch,
    isComplianceReady,
    isManuallyOpened,
    shouldAutoCloseInDeadlineMode,
  ])

  useEffect(() => {
    if (!isComplianceReady) return

    const prevShowPopup = prevShowPopupRef.current
    const currentShowPopup = Boolean(shouldShowPopup)
    const isAccessRestored = prevShowPopup === true && !currentShowPopup

    prevShowPopupRef.current = currentShowPopup

    if (!isAccessRestored) return

    void restorePlatformAccess()
  }, [isComplianceReady, restorePlatformAccess, shouldShowPopup])

  if (getBaseRole(role) !== 'children' && getBaseRole(role) !== 'parent') return null

  if (!shouldShowModal) return null

  const isBlockingAccess = !canClosePopup
  const popupTitle = activeCompliance?.popup_title
    ? String(activeCompliance.popup_title)
    : ''
  const deadlineSubtitle = getComplianceDeadlineSubtitle(
    activeCompliance?.deadline_date,
  )
  const popupSubtitle = deadlineSubtitle
    ? deadlineSubtitle
    : activeCompliance?.popup_subtitle
      ? String(activeCompliance.popup_subtitle)
      : ''
  const activeChildName =
    isParentSequentialFlow && activeChildCompliance?.child_name
      ? `Ученик: ${String(activeChildCompliance.child_name)}`
      : ''
  const title = isBlockingAccess
    ? 'Доступ к платформе закрыт'
    : DEADLINE_MODAL_TITLE
  const blockingSubtitle =
    popupSubtitle || popupTitle || BLOCKING_SUBTITLE_FALLBACK

  const handleLogout = async () => {
    if (loadingAccounts?.length) return

    const authorizedParentId = localStorage.getItem(
      idUserAuthorizedOnTheAuthorizationPage,
    )

    if (authorizedParentId && authorizedParentId !== String(profile?.id)) {
      await disconnectAccount(profile?.id)
      onLogin(authorizedParentId)
    } else {
      AuthService.logout()
    }
  }

  const dismissNonBlockingModal = () => {
    if (!canClosePopup || isSubmitting) return
    if (isManuallyOpened) {
      dispatch(closeModal({ type: TYPES.REQUIRED_DOCUMENTS_COMPLIANCE }))
    }
    setDismissedUntilTs(Date.now() + NON_BLOCKING_REOPEN_INTERVAL_MS)
  }

  const uploadDocument = async (document: ComplianceDocument, file: File) => {
    const documentName = document.name
    const formData = new FormData()
    formData.append(
      'message',
      `Загрузка обязательного документа: ${documentName}`,
    )
    formData.append('document_name', documentName)
    if (document.id) {
      formData.append('required_document_id', String(document.id))
    }
    formData.append('files', file)
    if (getBaseRole(role) === 'parent' && activeChildId != null && activeChildId > 0) {
      formData.append('user_id', String(activeChildId))
    }

    await usersAPI.sendDocumentForVerification(formData)
  }

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return

    const documentsToUpload = documents.filter((document) => {
      const fileKey = getComplianceDocumentFileKey(document, activeChildId)
      return Boolean(selectedFiles[fileKey])
    })

    setIsSubmitting(true)
    try {
      await Promise.all(
        documentsToUpload.map((document) => {
          const fileKey = getComplianceDocumentFileKey(document, activeChildId)
          return uploadDocument(document, selectedFiles[fileKey])
        }),
      )

      setSelectedFiles({})
      await refreshCompliance()
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('requiredDocumentsComplianceUpdated'))
      }
      addAlert({ title: 'Документы отправлены на проверку' })
    } catch {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось отправить документы. Попробуйте еще раз.',
        type: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return createPortal(
    <>
      {isBlockingAccess ? (
        <button
          type="button"
          className={classNames(
            headerClasses.button,
            classes.logoutButton,
            'trackClickWhilePerformingWork',
          )}
          title="Выйти"
          onClick={handleLogout}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M7 7.13193V6.61204C7 4.46614 7 3.3932 7.6896 2.79511C8.37919 2.19703 9.44136 2.34877 11.5657 2.65224L15.8485 3.26408C18.3047 3.61495 19.5327 3.79039 20.2664 4.63628C21 5.48217 21 6.72271 21 9.20377V14.7962C21 17.2773 21 18.5178 20.2664 19.3637C19.5327 20.2096 18.3047 20.385 15.8485 20.7359L11.5657 21.3478C9.44136 21.6512 8.37919 21.803 7.6896 21.2049C7 20.6068 7 19.5339 7 17.388V17.066"
              stroke="var(--color-text-main)"
              strokeWidth="1.5"
            />
            <path
              d="M16 12L16.5857 11.5315L16.9605 12L16.5857 12.4685L16 12ZM4 12.75C3.58579 12.75 3.25 12.4142 3.25 12C3.25 11.5858 3.58579 11.25 4 11.25V12.75ZM12.5857 6.53148L16.5857 11.5315L15.4143 12.4685L11.4143 7.46852L12.5857 6.53148ZM16.5857 12.4685L12.5857 17.4685L11.4143 16.5315L15.4143 11.5315L16.5857 12.4685ZM16 12.75H4V11.25H16V12.75Z"
              fill="var(--color-text-main)"
            />
          </svg>
        </button>
      ) : null}
      <Modal
      active={true}
      setActive={() => {
        dismissNonBlockingModal()
      }}
      disableBackdropDismiss={!canClosePopup}
      className={classes.modalWrapper as any}
      styleContent={{
        minWidth: 'auto',
        padding: 0,
      }}
    >
      <div className={sharedClasses.modalContainer}>
        <div className={sharedClasses.modalHeader}>
          <div className={sharedClasses.modalTitleContainer}>
            <span
              className={
                isBlockingAccess
                  ? classes.blockingTitle
                  : sharedClasses.modalHeaderTitle
              }
            >
              {title}
            </span>
          </div>
        </div>

        <div className={classes.contentWrapper}>
          {isBlockingAccess ? (
            <>
              {activeChildName ? (
                <p className={classes.childName}>{activeChildName}</p>
              ) : null}
              {blockingSubtitle ? (
                <p className={classes.subtitle}>{blockingSubtitle}</p>
              ) : null}
            </>
          ) : (
            <>
              {activeChildName ? (
                <p className={classes.childName}>{activeChildName}</p>
              ) : null}
              {popupSubtitle ? (
                <p className={classes.subtitle}>{popupSubtitle}</p>
              ) : null}
              {popupTitle ? (
                <p className={classes.subtitle}>{popupTitle}</p>
              ) : null}
            </>
          )}

          <ul className={classes.documentsList}>
            {documents.map((item, index) => {
              const state = (item?.state || 'not verified') as ComplianceDocumentState
              const fileKey = getComplianceDocumentFileKey(item, activeChildId)
              const supportComment = getDocumentSupportComment(item)
              const stateView = getComplianceDocumentStateView(item, {
                hasSelectedFile: Boolean(selectedFiles[fileKey]),
              })
              const hasSelectedFile = Boolean(selectedFiles[fileKey])
              const uploadButtonLabel =
                hasSelectedFile || item?.is_uploaded
                  ? 'Загрузить другой файл'
                  : 'Загрузить'

              return (
                <li
                  key={fileKey || `${item.name}-${index}`}
                  className={classNames(
                    classes.documentRow,
                    getDocumentRowClassByTone(stateView.tone),
                  )}
                >
                  <div className={classes.documentTop}>
                    <span className={classes.documentName}>{item.name}</span>
                    <span
                      className={classNames(
                        classes.stateBadge,
                        getDocumentStateClassByTone(stateView.tone),
                      )}
                    >
                      {stateView.label}
                    </span>
                    <input
                      ref={(element) => {
                        fileInputRefs.current[fileKey] = element
                      }}
                      type="file"
                      className={classes.hiddenInput}
                      onChange={(event) => {
                        const file = event.target.files?.[0]
                        if (!file) return
                        setSelectedFiles((prev) => ({
                          ...prev,
                          [fileKey]: file,
                        }))
                        event.currentTarget.value = ''
                      }}
                    />
                    <Button
                      type="button"
                      color="gray"
                      disabled={isSubmitting}
                      className={classes.uploadButton}
                      onClick={() => fileInputRefs.current[fileKey]?.click()}
                    >
                      {uploadButtonLabel}
                    </Button>
                  </div>
                  {supportComment && state === 'rejected' ? (
                    <ExpandableTextPanel
                      title=""
                      value={supportComment}
                      collapsible
                      tone="rejected"
                      appearance={COMPLIANCE_MODAL_APPEARANCE}
                    />
                  ) : null}
                </li>
              )
            })}
          </ul>

          <div className={classes.footerActions}>
            {canClosePopup ? (
              <Button
                type="button"
                color="secondary"
                disabled={isSubmitting}
                onClick={dismissNonBlockingModal}
              >
                Загрузить позже
              </Button>
            ) : null}
            <Button
              type="button"
              color="primary"
              disabled={!canSubmit || isSubmitting}
              isLoading={isSubmitting}
              onClick={handleSubmit}
            >
              Отправить
            </Button>
          </div>
        </div>
      </div>
    </Modal>
    </>,
    document.body,
  )
}

export default RequiredDocumentsComplianceModal
